import type { TravelHealthSnapshot, Tier1Snapshot, Tier1SelectionSnapshot, Tier2Snapshot, TravelHealthPhase } from "./types";

const SENTINEL_SECTION = "__travel_health_metadata__";

const GROUP_IDS = {
  metadata: "00000000-0000-4000-b000-000000000001",
  tier1: "00000000-0000-4000-b000-000000000002",
  tier1Selection: "00000000-0000-4000-b000-000000000004",
  tier2: "00000000-0000-4000-b000-000000000003",
} as const;

interface UserControl {
  label: string;
  value: string;
  type: string;
}

interface TemplateSection {
  groupId: string;
  sectionTitle: string;
  userControls: UserControl[];
}

interface ConsultationTemplate {
  templateName: string;
  companyID: string;
  templateSections: TemplateSection[];
}

function uc(label: string, value: unknown, type: string): UserControl {
  const v = typeof value === "string" ? value : JSON.stringify(value ?? null);
  return { label, value: v, type };
}

function buildSections(snapshot: TravelHealthSnapshot): TemplateSection[] {
  const sections: TemplateSection[] = [
    {
      groupId: GROUP_IDS.metadata,
      sectionTitle: SENTINEL_SECTION,
      userControls: [
        uc("version", snapshot.version, "number"),
        uc("phase", snapshot.phase, "string"),
        uc("savedAt", snapshot.savedAt, "string"),
      ],
    },
  ];

  if (snapshot.tier1) {
    sections.push({
      groupId: GROUP_IDS.tier1,
      sectionTitle: "__travel_health_tier1__",
      userControls: [
        uc("step", snapshot.tier1.step, "number"),
        uc("data", snapshot.tier1.data, "json"),
        uc("antimalarialTakenBefore", snapshot.tier1.antimalarialTakenBefore, "string"),
      ],
    });
  }

  if (snapshot.tier1Selection) {
    sections.push({
      groupId: GROUP_IDS.tier1Selection,
      sectionTitle: "__travel_health_tier1_selection__",
      userControls: [
        uc("tier1SelectionState", snapshot.tier1Selection, "json"),
      ],
    });
  }

  if (snapshot.tier2) {
    sections.push({
      groupId: GROUP_IDS.tier2,
      sectionTitle: "__travel_health_tier2__",
      userControls: [
        uc("tier2State", snapshot.tier2, "json"),
      ],
    });
  }

  return sections;
}

export function serializeToTemplate(
  snapshot: TravelHealthSnapshot,
  templateName: string,
  companyID: string,
): ConsultationTemplate {
  return {
    templateName,
    companyID,
    templateSections: buildSections(snapshot),
  };
}

export function serializeToSections(snapshot: TravelHealthSnapshot): TemplateSection[] {
  return buildSections(snapshot);
}

// ─────────────────────────────────────────────────────────────────────────────
// Deserialization
// ─────────────────────────────────────────────────────────────────────────────

function readControl(controls: UserControl[], label: string): string | undefined {
  return controls.find((c) => c.label === label)?.value;
}

function parseJson<T>(raw: string | undefined, fallback: T): T {
  if (raw === undefined || raw === "") return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function deserializeTravelHealthSnapshot(
  template: ConsultationTemplate | null | undefined,
): TravelHealthSnapshot | null {
  if (!template?.templateSections) return null;

  const metadataSection = template.templateSections.find(
    (s) => s.sectionTitle === SENTINEL_SECTION,
  );
  if (!metadataSection) return null;

  const mc = metadataSection.userControls;
  const version = Number(readControl(mc, "version") ?? "0");
  if (version !== 1) return null;

  const phase = (readControl(mc, "phase") ?? "") as TravelHealthPhase;
  if (!phase) return null;

  const savedAt = readControl(mc, "savedAt") ?? "";

  let tier1: Tier1Snapshot | undefined;
  const tier1Section = template.templateSections.find(
    (s) => s.sectionTitle === "__travel_health_tier1__",
  );
  if (tier1Section) {
    const tc = tier1Section.userControls;
    const step = Number(readControl(tc, "step") ?? "1");
    const data = parseJson(readControl(tc, "data"), null);
    const antimalarialTakenBefore = readControl(tc, "antimalarialTakenBefore") as
      | "yes"
      | "no"
      | "undefined"
      | undefined;

    if (data) {
      tier1 = {
        step,
        data,
        antimalarialTakenBefore:
          antimalarialTakenBefore === "undefined" || antimalarialTakenBefore === undefined
            ? undefined
            : antimalarialTakenBefore,
      };
    }
  }

  let tier1Selection: Tier1SelectionSnapshot | undefined;
  const tier1SelectionSection = template.templateSections.find(
    (s) => s.sectionTitle === "__travel_health_tier1_selection__",
  );
  if (tier1SelectionSection) {
    const tc = tier1SelectionSection.userControls;
    const parsed = parseJson<Tier1SelectionSnapshot | null>(
      readControl(tc, "tier1SelectionState"),
      null,
    );
    if (parsed && typeof parsed.step === "number") {
      tier1Selection = parsed;
    }
  }

  let tier2: Tier2Snapshot | undefined;
  const tier2Section = template.templateSections.find(
    (s) => s.sectionTitle === "__travel_health_tier2__",
  );
  if (tier2Section) {
    const tc = tier2Section.userControls;
    const parsed = parseJson<Tier2Snapshot | null>(readControl(tc, "tier2State"), null);
    if (parsed && parsed.planId && parsed.productId && parsed.pgdId) {
      tier2 = parsed;
    }
  }

  return { version: 1, savedAt, phase, tier1, tier1Selection, tier2 };
}
